/**
 * ActivityLogsModal Component
 *
 * A modal dialog that displays activity logs related to user IP address activity.
 * Shows the log entries in a data table format with timestamps and messages.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array<{timestamp: string, message: string}>} props.activityLogs - Array of activity log objects
 * @param {string} props.activityLogs[].timestamp - ISO timestamp of the log entry
 * @param {string} props.activityLogs[].message - Description of the activity
 *
 * @returns {React.ReactElement} A modal component with activity logs table
 *
 * @example
 * const logs = [
 *   { timestamp: '2023-12-01T10:30:00Z', message: 'Login successful' },
 *   { timestamp: '2023-12-01T09:15:00Z', message: 'Failed login attempt' }
 * ]
 * return <ActivityLogsModal activityLogs={logs} />
 *
 * Features:
 * - Toggle modal visibility with a clickable "View Activity Log" link
 * - Displays timestamps formatted as MM/DD/YYYY HH:mm:ss
 * - Shows warning message about contacting administrator for unfamiliar activity
 * - Responsive data table with left-aligned cells
 * - Fallback to '-' for missing timestamp or message data
 */

import { useState } from 'react'
import {
  Link,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Hint,
  DataTable,
  DataTableHead,
  DataTableRow,
  DataTableCell,
  DataTableHeadCell,
  DataTableBody,
} from '@americanexpress/dls-react'
import moment from 'moment'
import styles from './scss/Layout.module.scss'

export default function ActivityLogsModal({ activityLogs }) {
  const [isActivityLogsModalOpen, openActivityLogsModal] = useState(false)

  return (
    <>
      <span className={styles.activityLogBtn} onClick={() => openActivityLogsModal(true)}>
        View Activity Log
      </span>

      {isActivityLogsModalOpen && (
        <Modal
          className={styles.activityLogModalWrapper}
          aria-labelledby='activity-logs-modal'
          onClose={() => openActivityLogsModal(false)}
        >
          <ModalHeader id='activity-logs-modal' onClose={() => openActivityLogsModal(false)}>
            <h2>Activity Log</h2>
          </ModalHeader>
          <ModalBody id='activity-logs-modal'>
            <DataTable>
              <DataTableHead>
                <DataTableRow id='activity-logs-table-row-1'>
                  <DataTableHeadCell align='left'>Timestamp</DataTableHeadCell>
                  <DataTableHeadCell align='left'>Message</DataTableHeadCell>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {activityLogs.map((log, index) => (
                  <DataTableRow
                    id={`activity-logs-table-row-${index + 2}`}
                    key={`activity-logs-table-row-${index + 2}`}
                  >
                    <DataTableCell align='left'>
                      {log.timestamp
                        ? moment(new Date(log.timestamp)).format('MM/DD/YYYY HH:mm:ss')
                        : '-'}
                    </DataTableCell>
                    <DataTableCell align='left'>{log.message || '-'}</DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>

            {activityLogs.length === 0 && (
              <Hint style={{ marginTop: '0.5rem', marginBottom: '1rem', display: 'block' }}>
                No activity logs available for your IP address. If you have recently registered,
                please allow some time for the logs to be generated.
              </Hint>
            )}
          </ModalBody>
        </Modal>
      )}
    </>
  )
}
